import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetQuotationList,
  GetQuotationDetail,
  CreateQuotation,
  UpdateQuotation,
  DeleteQuotation,
} from "../../Services/SaleService";

/**
 * Quotation State Interface with caching and optimization
 */
export interface QuotationState {
  // List data
  quotationCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  
  // Detail data
  selectedQuotationDetail: any | null;
  
  // Loading states
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  
  // Error states
  error: string | null;
  errorDetail: string | null;
  
  // Metadata
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  
  // Cache management
  cache: Map<string, CacheEntry>;
  lastFetchParams: FetchParams | null;
  
  // Success flags for UX
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  financialYear: string;
  saleType: string;
  startDate: string;
  endDate: string;
}

const CACHE_DURATION = 5 * 60 * 1000;

const generateCacheKey = (params: FetchParams): string => {
  return `quotation_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.saleType}`;
};

const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

export const fetchQuotationList = createAsyncThunk(
  "quotation/fetchQuotationList",
  async (
    params: {
      consumerId: number;
      firmId: number;
      branchId: number;
      pageIndex: number;
      pageSize: number;
      sortColumn: string;
      sortDirection: string;
      searchValue: string;
      financialYear: string;
      saleType: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { quotation: QuotationState };
      const cacheKey = generateCacheKey(params);
      
      if (root.quotation.cache.has(cacheKey)) {
        const cached = root.quotation.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      urlParams.append("financialYear", params.financialYear);
      urlParams.append("saleType", params.saleType);
      urlParams.append("startDate", params.startDate);
      urlParams.append("endDate", params.endDate);

      const result: any = await GetQuotationList(urlParams);
      
      if (result?.data?.data?.quotationCollection) {
        return result.data.data;
      }
      
      return rejectWithValue("No quotation data received");
    } catch (error: any) {
      console.error("Error fetching quotation list:", error);
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch quotations"
      );
    }
  }
);

export const fetchQuotationDetail = createAsyncThunk(
  "quotation/fetchQuotationDetail",
  async (
    params: {
      consumerID: number;
      firmId: number;
      branchId: number;
      quotationID: number;
      financialYear: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append("consumerID", params.consumerID.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("quotationID", params.quotationID.toString());
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetQuotationDetail(urlParams);
      
      if (result?.data?.data) {
        return result.data.data;
      }
      
      return rejectWithValue("No quotation detail received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch quotation details"
      );
    }
  }
);

export const createQuotationAsync = createAsyncThunk(
  "quotation/createQuotation",
  async (quotationData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateQuotation(quotationData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to create quotation");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create quotation"
      );
    }
  }
);

export const updateQuotationAsync = createAsyncThunk(
  "quotation/updateQuotation",
  async (quotationData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateQuotation(quotationData);
      if (result?.data) return result.data;
      return rejectWithValue("Failed to update quotation");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update quotation"
      );
    }
  }
);

export const deleteQuotationAsync = createAsyncThunk(
  "quotation/deleteQuotation",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteQuotation(deleteData);
      if (result?.data) return deleteData;
      return rejectWithValue("Failed to delete quotation");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete quotation"
      );
    }
  }
);

const initialState: QuotationState = {
  quotationCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 10,
  selectedQuotationDetail: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "asc",
  searchValue: "",
  cache: new Map(),
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    clearQuotationData: (state) => {
      state.quotationCollection = [];
      state.totalCount = 0;
      state.selectedQuotationDetail = null;
      state.error = null;
    },
    clearQuotationDetail: (state) => {
      state.selectedQuotationDetail = null;
      state.errorDetail = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    invalidateCache: (state) => {
      state.cache.clear();
    },
    setFetchMetadata: (state, action: PayloadAction<Partial<FetchParams>>) => {
      state.searchValue = action.payload.searchValue || state.searchValue;
      state.sortColumn = action.payload.sortColumn || state.sortColumn;
      state.sortDirection = action.payload.sortDirection || state.sortDirection;
      state.pageIndex = action.payload.pageIndex || state.pageIndex;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuotationList.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchQuotationList.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.quotationCollection = action.payload?.quotationCollection || [];
        state.totalCount = action.payload?.totalCount || 0;
        state.pageIndex = action.payload?.pageIndex || 1;
        state.pageSize = action.payload?.pageSize || 10;
        if (state.lastFetchParams) {
          const cacheKey = generateCacheKey(state.lastFetchParams);
          state.cache.set(cacheKey, {
            data: action.payload,
            timestamp: Date.now(),
            expiresIn: CACHE_DURATION,
          });
        }
      })
      .addCase(fetchQuotationList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchQuotationDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(fetchQuotationDetail.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingDetail = false;
        state.selectedQuotationDetail = action.payload;
      })
      .addCase(fetchQuotationDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    builder
      .addCase(createQuotationAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createQuotationAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createQuotationAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateQuotationAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateQuotationAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateQuotationAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteQuotationAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteQuotationAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteQuotationAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearQuotationData,
  clearQuotationDetail,
  clearErrors,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = quotationSlice.actions;

export default quotationSlice.reducer;
